<script lang="ts" setup>
import countTo from 'vue-count-to'
import type { MemberTopupSubscribeProduct, SubscribePageCardScheme, SubscribePageConfig } from '../configs/types'
import { getSubscribePageConfig } from '../configs/brands'
import { VIP_TYPE } from '@/constants'

const props = defineProps<{
  item: MemberTopupSubscribeProduct
  getUnitPrice: (item: MemberTopupSubscribeProduct) => string | number
  isZero: (value: string | number | null | undefined) => boolean
}>()

const config: SubscribePageConfig = getSubscribePageConfig()

const defaultColorSchemes: Record<'discounted' | 'normal' | 'fakeDiscount', SubscribePageCardScheme> = {
  discounted: {
    bg: 'linear-gradient(270deg, #5E1A42 0%, #3A0E28 100%)',
    priceBg: 'linear-gradient(90deg, #F06292 0%, #C2185B 100%)',
    titleColor: '#F0EAFA',
    descColor: '#F5A8C8',
    borderColor: 'transparent',
    unitPriceColor: '#F5A8C8',
    originalPriceColor: '#F5A8C8',
    cycleLabelBg: 'linear-gradient(90deg, #6E2A52 0%, #5E1A42 100%)',
    cycleLabelColor: '#F0EAFA',
  },
  normal: {
    bg: 'linear-gradient(90deg, #3A1E5C 0%, #2F1F43 100%)',
    priceBg: 'linear-gradient(90deg, #8E47FF 0%, #6A2BFF 100%)',
    titleColor: '#F0EAFA',
    descColor: '#C7A7FF',
    borderColor: 'transparent',
    unitPriceColor: '#C7A7FF',
    originalPriceColor: '#C7A7FF',
    cycleLabelBg: 'linear-gradient(270deg, #3A1E5C 0%, #2F1F43 100%)',
    cycleLabelColor: '#F0EAFA',
  },
  fakeDiscount: {
    bg: 'linear-gradient(270deg, #5E1A42 0%, #3A0E28 100%)',
    priceBg: 'linear-gradient(90deg, #F06292 0%, #C2185B 100%)',
    titleColor: '#F0EAFA',
    descColor: '#F5A8C8',
    borderColor: 'transparent',
    unitPriceColor: '#F5A8C8',
    originalPriceColor: '#F5A8C8',
    cycleLabelBg: 'linear-gradient(270deg, #6E2A52 0%, #5E1A42 100%)',
    cycleLabelColor: '#F0EAFA',
  },
}

function getCardType(item: MemberTopupSubscribeProduct): 'discounted' | 'normal' | 'fakeDiscount' {
  if (item.preferentialPrice !== undefined && item.preferentialPrice !== null)
    return 'discounted'

  if (item.subscribeType === VIP_TYPE.DISCOUNT && item.discountOriginalPrice && Number(item.discountOriginalPrice) > 0)
    return 'fakeDiscount'

  return 'normal'
}

function getColorScheme(item: MemberTopupSubscribeProduct): SubscribePageCardScheme {
  const cardType = getCardType(item)
  return config.colors?.schemes?.[cardType] || defaultColorSchemes[cardType]
}

function getCycleLabel(item: MemberTopupSubscribeProduct) {
  if (item.subscribeType === VIP_TYPE.DISCOUNT)
    return `${item.cycleStr} Subscription`
  return item.cycleStr
}
</script>

<template>
  <div
    class="novaris-subscribe-item"
    :style="{
      background: getColorScheme(item).bg,
      borderColor: getColorScheme(item).borderColor,
    }"
  >
    <div
      v-if="item.cornerTip === 1 && item.marketingCopy"
      class="novaris-popular-tag"
      :style="{ background: config.colors.tagGradient || 'linear-gradient(132deg, #F961BC 0%, #F74984 100%)' }"
    >
      {{ item.marketingCopy }}
    </div>

    <div
      v-if="item.subscribeType === VIP_TYPE.DISCOUNT"
      class="novaris-cycle-label"
      :style="{
        background: getColorScheme(item).cycleLabelBg,
        color: getColorScheme(item).cycleLabelColor,
      }"
    >
      {{ getCycleLabel(item) }}
    </div>

    <template v-if="item.subscribeType === VIP_TYPE.DISCOUNT">
      <div class="novaris-card-content">
        <div class="novaris-card-left">
          <div class="novaris-title" :style="{ color: getColorScheme(item).titleColor }">
            {{ item.discountCoins }} coins
          </div>
          <ul class="novaris-coins-list">
            <li class="novaris-coins-item" :style="{ color: getColorScheme(item).descColor }">
              <span class="novaris-dot" :style="{ background: getColorScheme(item).descColor }" />
              <span>{{ config.texts.payTemplateGetCoins?.(item.discountOnceCoins) }}</span>
            </li>
            <li class="novaris-coins-item" :style="{ color: getColorScheme(item).descColor }">
              <span class="novaris-dot" :style="{ background: getColorScheme(item).descColor }" />
              <span>{{ config.texts.payTemplateEarnCoins?.(item.discountBonusCoins, item.discountDailyCoins) }}</span>
            </li>
          </ul>
        </div>
        <div class="novaris-card-right">
          <div class="novaris-price-btn" :style="{ background: getColorScheme(item).priceBg }">
            $<countTo
              :start-val="Number(item.discountOriginalPrice)"
              :end-val="Number(item.preferentialPrice || item.price)"
              :duration="3000"
              :decimals="2"
            />
          </div>
          <div class="novaris-original-price" :style="{ color: getColorScheme(item).originalPriceColor }">
            $ {{ item.discountOriginalPrice }}
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="novaris-card-content">
        <div class="novaris-card-left">
          <div class="novaris-title" :style="{ color: getColorScheme(item).titleColor }">
            {{ item.cycleStr }}
          </div>
          <div class="novaris-subscribe-desc" :style="{ color: getColorScheme(item).descColor }">
            {{ item.subscribeText }}
          </div>
        </div>
        <div class="novaris-card-right">
          <template v-if="isZero(item.preferentialPrice)">
            <div class="novaris-price-btn" :style="{ background: getColorScheme(item).priceBg }">
              {{ config.texts.free }}
            </div>
            <div class="novaris-original-price" :style="{ color: getColorScheme(item).originalPriceColor }">
              $ {{ item.price }}
            </div>
          </template>
          <template v-else-if="!item.preferentialPrice">
            <div class="novaris-price-btn" :style="{ background: getColorScheme(item).priceBg }">
              $ {{ item.price }}
            </div>
            <div v-if="getUnitPrice(item)" class="novaris-unit-price" :style="{ color: getColorScheme(item).unitPriceColor }">
              $ {{ getUnitPrice(item) }}
            </div>
          </template>
          <template v-else>
            <div class="novaris-price-btn" :style="{ background: getColorScheme(item).priceBg }">
              $<countTo
                :start-val="Number(item.price)"
                :end-val="Number(item.preferentialPrice)"
                :duration="3000"
                :decimals="2"
              />
            </div>
            <div class="novaris-original-price" :style="{ color: getColorScheme(item).originalPriceColor }">
              $ {{ item.price }}
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.novaris-subscribe-item {
  position: relative;
  width: 100%;
  min-height: 92px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.novaris-popular-tag {
  position: absolute;
  top: 0;
  left: 0;
  min-width: 49px;
  height: 16px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  border-radius: 8px 0 8px 0;
  font-weight: 500;
  font-size: 10px;
  color: #fff;
}

.novaris-cycle-label {
  position: absolute;
  top: 0;
  right: 0;
  width: 117px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 8px 0 8px;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: -0.1666666716px;
  font-family: HelveticaNeue, sans-serif;
  opacity: 0.9;
}

.novaris-card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.novaris-card-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.novaris-card-right {
  flex-shrink: 0;
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 5px;
}

.novaris-title {
  font-size: 18px;
  font-weight: 500;
  font-family: HelveticaNeue-Medium, sans-serif;
  line-height: 1;
  margin-bottom: 0;
  white-space: nowrap;
}

.novaris-coins-list {
  margin: 5px 0 0;
  padding: 0;
  list-style: none;
}

.novaris-coins-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  line-height: 14px;
  font-family: HelveticaNeue, sans-serif;
  margin-bottom: 2px;
}

.novaris-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  flex-shrink: 0;
  margin: 6px 0 4px;
}

.novaris-subscribe-desc {
  font-size: 14px;
  margin-top: 8px;
  line-height: 14px;
  font-family: HelveticaNeue, sans-serif;
}

.novaris-price-btn {
  width: 81px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  font-size: 14px;
  color: #fff;
  font-weight: 500;
  font-family: Inter-Medium, sans-serif;
}

.novaris-original-price {
  font-size: 14px;
  text-decoration: line-through;
  margin-top: 4px;
  font-family: HelveticaNeue, sans-serif;
  text-align: center;
  width: 100%;
}

.novaris-unit-price {
  font-size: 12px;
  margin-top: 4px;
  font-family: HelveticaNeue, sans-serif;
  text-align: center;
  width: 100%;
}
</style>
